import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Activity, ShieldCheck, FileEdit, KeyRound, LogIn, LogOut, UserPlus, RotateCw, User } from "lucide-react";

const KIND_ICON = {
  audit: FileEdit, security: ShieldCheck,
};
const ACTION_TONE = (a = "") => {
  const s = a.toLowerCase();
  if (s.includes("login_success")) return { icon: LogIn,   color: "text-emerald-600 bg-emerald-500/10" };
  if (s.includes("logout"))         return { icon: LogOut,  color: "text-slate-600 bg-slate-500/10" };
  if (s.includes("login_fail"))     return { icon: KeyRound, color: "text-red-600 bg-red-500/10" };
  if (s.includes("password"))       return { icon: KeyRound, color: "text-amber-600 bg-amber-500/10" };
  if (s.includes("delete"))         return { icon: FileEdit, color: "text-red-600 bg-red-500/10" };
  if (s.includes("create") || s.includes("add")) return { icon: UserPlus, color: "text-blue-600 bg-blue-500/10" };
  return { icon: Activity, color: "text-slate-600 bg-slate-500/10" };
};

const fmt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" });
};

/**
 * User Activity Timeline — merges audit_logs (business writes) + security_events
 * (auth successes / failures / password rotations) into one chronological feed.
 */
export default function ActivityTimeline() {
  const [users, setUsers]     = useState([]);
  const [selected, setSelected] = useState("");
  const [events, setEvents]   = useState([]);
  const [meta, setMeta]       = useState(null);
  const [busy, setBusy]       = useState(false);

  useEffect(() => {
    api.get("/users").then(r => {
      setUsers(r.data || []);
      if ((r.data || []).length) setSelected(r.data[0].id);
    });
  }, []);

  const load = async (uid) => {
    if (!uid) return;
    setBusy(true);
    try {
      const r = await api.get(`/activity/users/${uid}`, { params: { limit: 200 } });
      setEvents(r.data.events || []);
      setMeta(r.data.user || null);
    } catch { setEvents([]); }
    setBusy(false);
  };
  useEffect(() => { load(selected); }, [selected]);

  return (
    <div className="card-sharp p-5 space-y-4" data-testid="activity-timeline">
      <div className="flex items-center justify-between border-b pb-3 flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent flex items-center gap-1.5">
            <Activity className="w-3.5 h-3.5" /> User Activity Timeline
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Merged feed of business writes + auth events. Latest at top.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => load(selected)} disabled={busy}
            className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 border px-2 py-1 rounded-sm"
            data-testid="activity-refresh">
            <RotateCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} /> Refresh
          </button>
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger className="w-72 rounded-sm h-9" data-testid="activity-user-picker">
              <SelectValue placeholder="Pick a user…" />
            </SelectTrigger>
            <SelectContent>
              {users.map(u => (
                <SelectItem key={u.id} value={u.id} data-testid={`activity-user-opt-${u.id}`}>
                  {u.full_name || u.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {meta && (
        <Card className="p-3 rounded-sm bg-slate-50 border flex items-center gap-3">
          <User className="w-8 h-8 text-slate-500" />
          <div>
            <div className="font-semibold">{meta.full_name || "—"}</div>
            <div className="text-[11px] font-mono text-muted-foreground">{meta.email}</div>
          </div>
          <div className="ml-auto text-[11px] font-mono text-muted-foreground">{events.length} events</div>
        </Card>
      )}

      <ol className="relative border-l-2 border-slate-200 space-y-3 pl-5" data-testid="activity-events">
        {events.length === 0 && (
          <li className="text-sm text-muted-foreground italic py-6">
            {busy ? "Loading…" : "No activity for this user yet."}
          </li>
        )}
        {events.map((e, i) => {
          const { icon: Icon, color } = ACTION_TONE(e.action);
          return (
            <li key={i} className="relative" data-testid={`activity-event-${i}`}>
              <div className={`absolute -left-[30px] w-6 h-6 rounded-full flex items-center justify-center ${color}`}>
                <Icon className="w-3 h-3" />
              </div>
              <div className="text-sm">
                <span className="font-semibold">{e.action}</span>
                <span className="text-muted-foreground"> · </span>
                <span className="font-mono text-[11px]">{e.entity}{e.entity_id ? `/${String(e.entity_id).slice(0, 8)}` : ""}</span>
                <span className={`ml-2 text-[9px] font-mono uppercase px-1.5 py-0.5 rounded-sm ${e.kind === "audit" ? "bg-blue-500/10 text-blue-600" : "bg-purple-500/10 text-purple-600"}`}>
                  {e.kind}
                </span>
              </div>
              <div className="text-[11px] font-mono text-muted-foreground">
                {fmt(e.at)}
                {e.meta?.ip && <span> · IP {e.meta.ip}</span>}
                {e.meta?.role && <span> · Role {e.meta.role}</span>}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
